import React, { useEffect, useState } from 'react';
import useApiStable from '../../utils/useApiStable';
import { useAuth } from '../../context/AuthContext';
import { canManageWorkDuty } from '../../utils/userUtils';
import WorkDutyAppointmentForm from './WorkDutyAppointmentForm';
import '../../styles/TaskList.css';

function formatDate(dateStr) {
  const [y, m, d] = dateStr.split('-');
  return `${d}.${m}.${y}`;
}

function ParticipantsModal({ appointmentId, onClose }) {
  const api = useApiStable();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/workduty/appointments/${appointmentId}/participants`)
      .then(data => setParticipants(Array.isArray(data) ? data : []))
      .catch(err => setError(err.message || 'Fehler beim Laden der Teilnehmerliste'))
      .finally(() => setLoading(false));
  }, [appointmentId]); // eslint-disable-line

  const confirmed = participants.filter(p => p.status === 'CONFIRMED');
  const declined = participants.filter(p => p.status === 'DECLINED');

  return (
    <div className="taskform-card">
      <h3 style={{ marginBottom: '18px' }}>Zu-/Absagen</h3>
      {error && <div className="taskform-error">{error}</div>}
      {loading ? <p>Lade...</p> : (
        <>
          <p><strong>Zugesagt ({confirmed.length})</strong></p>
          <ul className="tasklist-list">
            {confirmed.map(p => <li className="tasklist-item" key={p.personId}>{p.personName}</li>)}
            {confirmed.length === 0 && <li className="tasklist-item">Noch niemand.</li>}
          </ul>
          <p style={{ marginTop: 16 }}><strong>Abgesagt ({declined.length})</strong></p>
          <ul className="tasklist-list">
            {declined.map(p => <li className="tasklist-item" key={p.personId}>{p.personName}</li>)}
            {declined.length === 0 && <li className="tasklist-item">Niemand.</li>}
          </ul>
        </>
      )}
      <div className="taskform-actions">
        <button type="button" className="taskform-cancel-btn" onClick={onClose}>Schließen</button>
      </div>
    </div>
  );
}

function WorkDutyPage() {
  const { user } = useAuth();
  const api = useApiStable();
  const manage = canManageWorkDuty(user);

  const [appointments, setAppointments] = useState([]);
  const [error, setError] = useState('');
  const [respondingId, setRespondingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [participantsAppointmentId, setParticipantsAppointmentId] = useState(null);

  const loadAppointments = () => {
    setError('');
    api.get('/workduty/appointments')
      .then(data => setAppointments(Array.isArray(data) ? data : []))
      .catch(err => setError(err.message || 'Fehler beim Laden der Termine'));
  };

  useEffect(() => {
    loadAppointments();
  }, []); // eslint-disable-line

  const handleRespond = (appointmentId, status) => {
    setRespondingId(appointmentId);
    setError('');
    api.post(`/workduty/appointments/${appointmentId}/respond`, { status })
      .then(loadAppointments)
      .catch(err => setError(err.message || 'Fehler beim Speichern der Antwort'))
      .finally(() => setRespondingId(null));
  };

  const handleFormClose = (changed) => {
    setShowForm(false);
    setSelectedAppointment(null);
    if (changed) loadAppointments();
  };

  return (
    <div className="tasklist-container">
      <div className="tasklist-header">
        <h2>Arbeitsdienste</h2>
        {manage && <button className="tasklist-btn" onClick={() => { setSelectedAppointment(null); setShowForm(true); }}>Termin anlegen</button>}
      </div>
      {error && <div style={{ color: '#c62828', fontSize: '0.85em', marginBottom: '8px' }}>{error}</div>}
      <ul className="tasklist-list">
        {appointments.map(a => {
          const reached = a.confirmedCount >= a.minParticipants;
          const canViewParticipants = manage || (user?.personId != null && a.responsiblePersonId === user.personId);
          return (
            <li className="tasklist-item" key={a.id} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div className="tasklist-item-info">
                <strong>{a.title}</strong> — {formatDate(a.date)}, {a.timeFrom.substring(0, 5)}
                {a.timeTo ? `–${a.timeTo.substring(0, 5)}` : ''} Uhr
                <br />
                <span style={{ color: '#666', fontSize: '0.95em' }}>{a.description}</span>
                <br />
                <span style={{ color: '#666', fontSize: '0.9em' }}>Verantwortlich: {a.responsiblePersonName}</span>
                <br />
                <span style={{ color: reached ? '#2e7d32' : '#666', fontWeight: reached ? 'bold' : 'normal', fontSize: '0.9em' }}>
                  {a.confirmedCount} von mind. {a.minParticipants} zugesagt
                </span>
                {a.myStatus && (
                  <span style={{ marginLeft: 8, fontSize: '0.85em', color: a.myStatus === 'CONFIRMED' ? '#2e7d32' : '#e65100' }}>
                    (Meine Antwort: {a.myStatus === 'CONFIRMED' ? 'Zugesagt' : 'Abgesagt'})
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <button className="tasklist-edit-btn" disabled={respondingId === a.id}
                        onClick={() => handleRespond(a.id, 'CONFIRMED')}>Zusagen</button>
                <button className="tasklist-edit-btn" style={{ background: '#888' }} disabled={respondingId === a.id}
                        onClick={() => handleRespond(a.id, 'DECLINED')}>Absagen</button>
                {canViewParticipants && (
                  <button className="tasklist-edit-btn" style={{ background: '#35424a' }}
                          onClick={() => setParticipantsAppointmentId(a.id)}>Teilnehmer</button>
                )}
                {manage && (
                  <button className="tasklist-edit-btn" style={{ background: '#35424a' }}
                          onClick={() => { setSelectedAppointment(a); setShowForm(true); }}>Bearbeiten</button>
                )}
              </div>
            </li>
          );
        })}
        {appointments.length === 0 && <li className="tasklist-item">Aktuell keine anstehenden Arbeitsdienste.</li>}
      </ul>
      {showForm && <WorkDutyAppointmentForm appointment={selectedAppointment} onClose={handleFormClose} />}
      {participantsAppointmentId && (
        <ParticipantsModal appointmentId={participantsAppointmentId} onClose={() => setParticipantsAppointmentId(null)} />
      )}
    </div>
  );
}

export default WorkDutyPage;
